import apiClient, { PaginatedResponse } from './client';

export type AppStatus = 'active' | 'inactive' | 'deploying' | 'failed' | 'maintenance';
export type AppType = 'container' | 'static';
export type MaintenanceMode = 'maintenance' | 'coming_soon' | null;
export type DeploymentStatus = 'pending' | 'in_progress' | 'success' | 'failed' | 'rolled_back';

export interface App {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  server_id: string;
  app_type: AppType;
  docker_compose_path: string | null;
  service_name: string | null;
  static_path: string | null;
  env_file_path: string | null;
  git_repo: string | null;
  git_branch: string;
  domain: string | null;
  status: AppStatus;
  // Maintenance mode
  maintenance_mode: MaintenanceMode;
  maintenance_message: string | null;
  whitelist_ips: string[] | null;
  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface AppCreate {
  name: string;
  slug: string;
  description?: string;
  server_id: string;
  app_type?: AppType;
  docker_compose_path?: string;
  service_name?: string;
  static_path?: string;
  env_file_path?: string;
  git_repo?: string;
  git_branch?: string;
  domain?: string;
}

export interface AppUpdate {
  name?: string;
  description?: string;
  app_type?: AppType;
  docker_compose_path?: string;
  service_name?: string;
  static_path?: string;
  env_file_path?: string;
  git_repo?: string;
  git_branch?: string;
  domain?: string;
  status?: AppStatus;
  // Maintenance mode
  maintenance_mode?: MaintenanceMode;
  maintenance_message?: string;
  whitelist_ips?: string[];
}

export interface Deployment {
  id: string;
  app_id: string;
  version: string;
  commit_hash: string | null;
  commit_message: string | null;
  status: DeploymentStatus;
  logs: string | null;
  error_message: string | null;
  triggered_by: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface Environment {
  id: string;
  app_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Secret {
  id: string;
  environment_id: string;
  key: string;
  masked_value: string;
  description: string | null;
  is_sensitive: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListAppsParams {
  page?: number;
  limit?: number;
  search?: string;
  server_id?: string;
  status?: AppStatus;
}

// Container types
export interface ContainerApp {
  id: string;
  name: string;
  slug: string;
}

export interface Container {
  container_id: string;
  name: string;
  image: string;
  status: string;
  state: string;
  ports: string[];
  created: string;
  // Docker Compose info
  compose_project: string | null;
  compose_service: string | null;
  compose_path: string | null;
  // Domain info
  domain: string | null;
  // Linked app
  app: ContainerApp | null;
}

export interface ActionResult {
  success: boolean;
  message?: string;
}

export interface LogsResult {
  success: boolean;
  logs: string;
}

export interface DeployResult {
  success: boolean;
  logs: string[];
  error?: string;
}

export interface RebuildJobResult {
  job_id: string;
  status: string;
}

export interface JobStatus {
  job_id: string;
  status: 'running' | 'success' | 'failed' | 'not_found';
  started_at: string | null;
  completed_at: string | null;
  output: string[];
}

export interface SyncResult {
  success: boolean;
  message: string;
  secrets_count: number;
  env_path: string;
}

export interface SecretActivity {
  id: string;
  action: 'create' | 'update' | 'delete' | 'sync' | 'import';
  secret_key: string | null;
  old_value_masked: string | null;
  new_value_masked: string | null;
  details: string | null;
  user_email: string | null;
  created_at: string;
}

// Domain setup types
export interface DomainSetupRequest {
  domain: string;
  upstream_port?: number;
  ssl_enabled?: boolean;
}

export interface DomainSetupResponse {
  success: boolean;
  message: string;
  domain_id: string | null;
  ssl_certificate_id: string | null;
  nginx_deployed: boolean;
}

export interface AppDomain {
  id: string;
  name: string;
  server_id: string;
  app_id: string | null;
  dns_provider: string | null;
  dns_record_type: string;
  dns_target: string | null;
  status: 'pending' | 'active' | 'inactive' | 'error';
  ssl_enabled: boolean;
  ssl_auto_renew: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppSSLCertificate {
  id: string;
  domain_id: string;
  issuer: string | null;
  serial_number: string | null;
  status: 'pending' | 'active' | 'expired' | 'error';
  issued_at: string | null;
  expires_at: string | null;
  cert_path: string | null;
  key_path: string | null;
  created_at: string;
  updated_at: string;
}

export const appsApi = {
  list: async (params: ListAppsParams = {}): Promise<PaginatedResponse<App>> => {
    const response = await apiClient.get<PaginatedResponse<App>>('/api/v1/apps', { params });
    return response.data;
  },

  get: async (id: string): Promise<App> => {
    const response = await apiClient.get<App>(`/api/v1/apps/${id}`);
    return response.data;
  },

  create: async (data: AppCreate): Promise<App> => {
    const response = await apiClient.post<App>('/api/v1/apps', data);
    return response.data;
  },

  update: async (id: string, data: AppUpdate): Promise<App> => {
    const response = await apiClient.patch<App>(`/api/v1/apps/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/apps/${id}`);
  },

  // Deployments
  listDeployments: async (appId: string, params: { page?: number; limit?: number } = {}): Promise<PaginatedResponse<Deployment>> => {
    const response = await apiClient.get<PaginatedResponse<Deployment>>(`/api/v1/apps/${appId}/deployments`, { params });
    return response.data;
  },

  deploy: async (appId: string, data: { version: string; commit_hash?: string; commit_message?: string }): Promise<Deployment> => {
    const response = await apiClient.post<Deployment>(`/api/v1/apps/${appId}/deploy`, data);
    return response.data;
  },

  rollback: async (appId: string, deploymentId: string): Promise<Deployment> => {
    const response = await apiClient.post<Deployment>(`/api/v1/apps/${appId}/deployments/${deploymentId}/rollback`);
    return response.data;
  },

  // Environments
  listEnvironments: async (appId: string): Promise<Environment[]> => {
    const response = await apiClient.get<Environment[]>(`/api/v1/apps/${appId}/environments`);
    return response.data;
  },

  createEnvironment: async (appId: string, data: { name: string }): Promise<Environment> => {
    const response = await apiClient.post<Environment>(`/api/v1/apps/${appId}/environments`, data);
    return response.data;
  },

  updateEnvironment: async (appId: string, envId: string, data: { name: string }): Promise<Environment> => {
    const response = await apiClient.patch<Environment>(`/api/v1/apps/${appId}/environments/${envId}`, data);
    return response.data;
  },

  deleteEnvironment: async (appId: string, envId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/apps/${appId}/environments/${envId}`);
  },

  // Secrets
  listSecrets: async (appId: string, envId: string): Promise<Secret[]> => {
    const response = await apiClient.get<Secret[]>(`/api/v1/apps/${appId}/environments/${envId}/secrets`);
    return response.data;
  },

  createSecret: async (appId: string, envId: string, data: { key: string; value: string; description?: string; is_sensitive?: boolean }): Promise<Secret> => {
    const response = await apiClient.post<Secret>(`/api/v1/apps/${appId}/environments/${envId}/secrets`, data);
    return response.data;
  },

  deleteSecret: async (appId: string, envId: string, secretId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/apps/${appId}/environments/${envId}/secrets/${secretId}`);
  },

  getSecretValue: async (appId: string, envId: string, secretId: string): Promise<{ key: string; value: string }> => {
    const response = await apiClient.get<{ key: string; value: string }>(`/api/v1/apps/${appId}/environments/${envId}/secrets/${secretId}/value`);
    return response.data;
  },

  updateSecret: async (appId: string, envId: string, secretId: string, data: { key?: string; value?: string; description?: string; is_sensitive?: boolean }): Promise<Secret> => {
    const response = await apiClient.patch<Secret>(`/api/v1/apps/${appId}/environments/${envId}/secrets/${secretId}`, data);
    return response.data;
  },

  // Containers
  listContainers: async (serverId: string): Promise<Container[]> => {
    const response = await apiClient.get<{ containers: Container[] }>(`/api/v1/apps/containers/${serverId}`);
    return response.data.containers;
  },

  // App Actions
  startApp: async (appId: string): Promise<ActionResult> => {
    const response = await apiClient.post<ActionResult>(`/api/v1/apps/${appId}/actions/start`);
    return response.data;
  },

  stopApp: async (appId: string): Promise<ActionResult> => {
    const response = await apiClient.post<ActionResult>(`/api/v1/apps/${appId}/actions/stop`);
    return response.data;
  },

  restartApp: async (appId: string): Promise<ActionResult> => {
    const response = await apiClient.post<ActionResult>(`/api/v1/apps/${appId}/actions/restart`);
    return response.data;
  },

  rebuildApp: async (appId: string): Promise<RebuildJobResult> => {
    const response = await apiClient.post<RebuildJobResult>(`/api/v1/apps/${appId}/actions/rebuild`);
    return response.data;
  },

  getJobStatus: async (appId: string, jobId: string): Promise<JobStatus> => {
    const response = await apiClient.get<JobStatus>(`/api/v1/apps/${appId}/jobs/${jobId}`);
    return response.data;
  },

  deployApp: async (appId: string): Promise<DeployResult> => {
    const response = await apiClient.post<DeployResult>(`/api/v1/apps/${appId}/actions/deploy`);
    return response.data;
  },

  getLogs: async (appId: string, lines: number = 100): Promise<LogsResult> => {
    const response = await apiClient.get<LogsResult>(`/api/v1/apps/${appId}/logs`, { params: { lines } });
    return response.data;
  },

  getStatus: async (appId: string): Promise<{ running: boolean; state?: string; started_at?: string }> => {
    const response = await apiClient.get(`/api/v1/apps/${appId}/status`);
    return response.data;
  },

  getComposeFile: async (appId: string): Promise<{ success: boolean; content?: string; path?: string; error?: string }> => {
    const response = await apiClient.get(`/api/v1/apps/${appId}/compose`);
    return response.data;
  },

  getEnvFile: async (appId: string, customPath?: string): Promise<{ success: boolean; variables: Array<{ key: string; value: string }>; path?: string; error?: string }> => {
    const response = await apiClient.get(`/api/v1/apps/${appId}/env-file`, {
      params: customPath ? { path: customPath } : undefined,
    });
    return response.data;
  },

  // Secrets sync & activity
  syncSecretsToServer: async (appId: string, envId: string): Promise<SyncResult> => {
    const response = await apiClient.post<SyncResult>(`/api/v1/apps/${appId}/environments/${envId}/sync`);
    return response.data;
  },

  getSecretActivity: async (appId: string, envId: string, limit: number = 50): Promise<SecretActivity[]> => {
    const response = await apiClient.get<SecretActivity[]>(`/api/v1/apps/${appId}/environments/${envId}/activity`, {
      params: { limit },
    });
    return response.data;
  },

  // Domain management
  getDomain: async (appId: string): Promise<AppDomain | null> => {
    const response = await apiClient.get<AppDomain | null>(`/api/v1/apps/${appId}/domain`);
    return response.data;
  },

  getDomains: async (appId: string): Promise<Array<{
    id: string;
    domain_id: string;
    app_id: string;
    path: string;
    upstream_port: number;
    upstream_host: string;
    priority: number;
    is_active: boolean;
    app_name?: string | null;
    created_at: string;
    updated_at: string;
  }>> => {
    const response = await apiClient.get(`/api/v1/apps/${appId}/domains`);
    return response.data;
  },

  getSSLCertificate: async (appId: string): Promise<AppSSLCertificate | null> => {
    const response = await apiClient.get<AppSSLCertificate | null>(`/api/v1/apps/${appId}/domain/ssl`);
    return response.data;
  },

  setupDomain: async (appId: string, data: DomainSetupRequest): Promise<DomainSetupResponse> => {
    const response = await apiClient.post<DomainSetupResponse>(`/api/v1/apps/${appId}/domain/setup`, data);
    return response.data;
  },

  issueSSL: async (appId: string): Promise<AppSSLCertificate> => {
    const response = await apiClient.post<AppSSLCertificate>(`/api/v1/apps/${appId}/domain/ssl/issue`);
    return response.data;
  },
};
